import { jsPDF } from 'jspdf';

class DownloadService {
  /**
   * Get a file name based on selection criteria and file type
   * @param {Object} options - Options for file naming
   * @param {string} options.filePrefix - Prefix for the filename
   * @param {string} options.extension - File extension (pdf, xml, csv)
   * @param {string} options.screenName - Name of the current screen
   * @returns {string} - Generated filename
   */
  static getFileName({ filePrefix, extension, screenName }) {
    const date = new Date();
    let fileName = filePrefix;
    
    // Add screen name if provided
    if (screenName) {
      fileName = `${screenName}_${fileName}`;
    }
    
    // Add timestamp
    fileName += `${date.getTime()}.${extension}`;
    
    return fileName;
  }

  /**
   * Calculate optimal column widths based on content
   * @param {Array} headers - Array of header strings
   * @param {Array} data - Array of data rows
   * @param {number} availableWidth - Available width for the table
   * @param {Object} pdf - jsPDF instance for text width calculation
   * @param {number} fontSize - Font size for calculations
   * @returns {Array} - Array of column widths
   */
  static calculateColumnWidths(headers, data, availableWidth, pdf, fontSize) {
    pdf.setFontSize(fontSize);
    
    // Calculate the maximum width needed for each column
    const columnWidths = headers.map((header, colIndex) => {
      // Start with header width
      let maxWidth = pdf.getTextWidth(header) + 6; // Add padding
      
      // Check each data row for this column
      data.forEach(row => {
        const cellValue = String(row[colIndex] || '');
        const cellWidth = pdf.getTextWidth(cellValue) + 6; // Add padding
        maxWidth = Math.max(maxWidth, cellWidth);
      });
      
      return maxWidth;
    });
    
    // Calculate total required width
    const totalRequiredWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    
    // If total required width exceeds available width, scale down proportionally
    if (totalRequiredWidth > availableWidth) {
      const scaleFactor = availableWidth / totalRequiredWidth;
      return columnWidths.map(width => width * scaleFactor);
    }
    
    // If we have extra space, distribute it proportionally
    const extraSpace = availableWidth - totalRequiredWidth;
    const totalWeight = columnWidths.reduce((sum, width) => sum + width, 0);
    
    return columnWidths.map(width => {
      const proportion = width / totalWeight;
      return width + (extraSpace * proportion);
    });
  }

  /**
   * Wrap text to fit within a given width
   * @param {Object} pdf - jsPDF instance
   * @param {string} text - Text to wrap
   * @param {number} maxWidth - Maximum width available
   * @param {number} fontSize - Font size
   * @returns {Array} - Array of text lines
   */
  static wrapText(pdf, text, maxWidth, fontSize) {
    pdf.setFontSize(fontSize);
    const words = String(text).split(' ');
    const lines = [];
    let currentLine = '';
    
    for (let word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = pdf.getTextWidth(testLine);
      
      if (testWidth <= maxWidth - 6) { // Account for padding
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // Single word is too long, break it
          lines.push(word);
        }
      }
    }
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines.length > 0 ? lines : [''];
  }

  /**
   * Download data as CSV file
   * @param {Object} options - Options for downloading
   * @param {string} options.csvContent - CSV content to download
   * @param {string} options.filePrefix - Prefix for the filename
   * @param {string} options.screenName - Name of the current screen
   * @param {function} options.onStart - Callback when download starts
   * @param {function} options.onComplete - Callback when download completes
   * @param {function} options.onError - Callback when download fails
   * @returns {Promise<void>}
   */
  static async downloadCSV({ csvContent, filePrefix, screenName, onStart, onComplete, onError }) {
    if (!csvContent) {
      alert('No data available to download');
      return;
    }

    onStart && onStart();
    
    try {
      const fileName = this.getFileName({ filePrefix, extension: 'csv', screenName });
      
      // Create a Blob from the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create a download link
      this.downloadFile(blob, fileName);
      
      onComplete && onComplete(fileName);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download data as CSV');
      onError && onError(error);
    }
  }

  /**
   * Download data as XML file
   * @param {Object} options - Options for downloading
   * @param {string} options.xmlContent - XML content to download
   * @param {string} options.filePrefix - Prefix for the filename
   * @param {string} options.screenName - Name of the current screen
   * @param {function} options.onStart - Callback when download starts
   * @param {function} options.onComplete - Callback when download completes
   * @param {function} options.onError - Callback when download fails
   * @returns {Promise<void>}
   */
  static async downloadXML({ xmlContent, filePrefix, screenName, onStart, onComplete, onError }) {
    if (!xmlContent) {
      alert('No data available to download');
      return;
    }

    onStart && onStart();
    
    try {
      const fileName = this.getFileName({ filePrefix, extension: 'xml', screenName });
      
      // Create a Blob from the XML content
      const blob = new Blob([xmlContent], { type: 'application/xml;charset=utf-8;' });
      
      // Create a download link
      this.downloadFile(blob, fileName);
      
      onComplete && onComplete(fileName);
    } catch (error) {
      console.error('Error downloading XML:', error);
      alert('Failed to download data as XML');
      onError && onError(error);
    }
  }

  /**
   * Download data as PDF file
   * @param {Object} options - Options for downloading
   * @param {string} options.title - Title for the PDF document
   * @param {Array} options.columns - Column definitions for the table
   * @param {Array} options.data - Data to be displayed in the PDF
   * @param {string} options.filePrefix - Prefix for the filename
   * @param {string} options.screenName - Name of the current screen
   * @param {function} options.onStart - Callback when download starts
   * @param {function} options.onComplete - Callback when download completes
   * @param {function} options.onError - Callback when download fails
   * @returns {Promise<void>}
   */
  static async downloadPDF({ title, columns, data, filePrefix, screenName, onStart, onComplete, onError }) {
    if (!data || data.length === 0) {
      alert('No data available to download');
      return;
    }

    onStart && onStart();
    
    try {
      // Create a new jsPDF document in landscape mode for better table fit
      const pdf = new jsPDF({
        orientation: columns.length > 6 ? 'landscape' : 'portrait',
        unit: 'pt',
        format: 'a4'
      });
      
      // Get page dimensions
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 40;
      
      // Add title to PDF
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      const titleWidth = pdf.getTextWidth(title);
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 30);
      
      // Add generation date
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      const dateText = `Generated on: ${new Date().toLocaleString()}`;
      const dateWidth = pdf.getTextWidth(dateText);
      pdf.text(dateText, pageWidth - margin - dateWidth, 50);
      
      // Prepare table data
      const headers = columns.map(col => col.header);
      const rows = data.map((row, rowIndex) => {
        return columns.map(column => {
          if (column.value === 'index') {
            return String(rowIndex + 1);
          }
          return String(row[column.value] !== undefined ? row[column.value] : '');
        });
      });
      
      // Define table settings
      const startY = 70;
      const minRowHeight = 20;
      const headerFontSize = 10;
      const contentFontSize = 9;
      const cellPadding = 4;
      const headerColor = [44, 82, 130]; // Blue color
      const alternateRowColor = [248, 250, 252]; // Very light gray
      const borderColor = [200, 200, 200]; // Light gray for borders
      
      // Calculate available width for table
      const tableWidth = pageWidth - (2 * margin);
      
      // Calculate optimal column widths
      const columnWidths = this.calculateColumnWidths(headers, rows, tableWidth, pdf, contentFontSize);
      
      // Draw table header
      let currentY = startY;
      
      // Header background
      pdf.setFillColor(...headerColor);
      pdf.rect(margin, currentY, tableWidth, minRowHeight + 10, 'F');
      
      // Header borders
      pdf.setDrawColor(...borderColor);
      pdf.setLineWidth(0.5);
      let currentX = margin;
      
      // Draw vertical lines for header
      columnWidths.forEach((width, index) => {
        if (index === 0) {
          pdf.line(currentX, currentY, currentX, currentY + minRowHeight + 10); // Left border
        }
        currentX += width;
        pdf.line(currentX, currentY, currentX, currentY + minRowHeight + 10); // Right border of each column
      });
      
      // Header horizontal lines
      pdf.line(margin, currentY, margin + tableWidth, currentY); // Top border
      pdf.line(margin, currentY + minRowHeight + 10, margin + tableWidth, currentY + minRowHeight + 10); // Bottom border
      
      // Header text
      pdf.setFontSize(headerFontSize);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(255, 255, 255); // White text
      
      currentX = margin;
      headers.forEach((header, index) => {
        const textX = currentX + cellPadding;
        const textY = currentY + (minRowHeight + 10) / 2 + 4; // Center vertically
        
        // Wrap header text if needed
        const wrappedText = this.wrapText(pdf, header, columnWidths[index], headerFontSize);
        pdf.text(wrappedText[0], textX, textY); // Only show first line for headers
        
        currentX += columnWidths[index];
      });
      
      currentY += minRowHeight + 10;
      
      // Draw data rows
      pdf.setFontSize(contentFontSize);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(0, 0, 0); // Black text
      
      rows.forEach((row, rowIndex) => {
        // Calculate row height based on content
        let maxLines = 1;
        const wrappedRowData = row.map((cell, colIndex) => {
          const wrapped = this.wrapText(pdf, cell, columnWidths[colIndex], contentFontSize);
          maxLines = Math.max(maxLines, wrapped.length);
          return wrapped;
        });
        
        const rowHeight = Math.max(minRowHeight, maxLines * 12 + 8);
        
        // Check if we need a new page
        if (currentY + rowHeight > pageHeight - margin) {
          pdf.addPage();
          currentY = margin;
        }
        
        // Alternate row background
        if (rowIndex % 2 === 1) {
          pdf.setFillColor(...alternateRowColor);
          pdf.rect(margin, currentY, tableWidth, rowHeight, 'F');
        }
        
        // Draw cell borders
        pdf.setDrawColor(...borderColor);
        currentX = margin;
        
        // Vertical lines
        columnWidths.forEach((width, index) => {
          if (index === 0) {
            pdf.line(currentX, currentY, currentX, currentY + rowHeight); // Left border
          }
          currentX += width;
          pdf.line(currentX, currentY, currentX, currentY + rowHeight); // Right border
        });
        
        // Horizontal lines
        pdf.line(margin, currentY, margin + tableWidth, currentY); // Top border
        pdf.line(margin, currentY + rowHeight, margin + tableWidth, currentY + rowHeight); // Bottom border
        
        // Draw cell content
        currentX = margin;
        row.forEach((cell, colIndex) => {
          const textX = currentX + cellPadding;
          const wrappedText = wrappedRowData[colIndex];
          
          // Draw each line of wrapped text
          wrappedText.forEach((line, lineIndex) => {
            const textY = currentY + cellPadding + 8 + (lineIndex * 12);
            pdf.text(line, textX, textY);
          });
          
          currentX += columnWidths[colIndex];
        });
        
        currentY += rowHeight;
      });
      
      // Save the PDF
      const fileName = this.getFileName({ filePrefix, extension: 'pdf', screenName });
      pdf.save(fileName);
      
      onComplete && onComplete(fileName);
    } catch (error) {
      console.error('Error creating PDF:', error);
      alert('Failed to generate PDF file: ' + error.message);
      onError && onError(error);
    }
  }

  /**
   * Helper method to download a file
   * @param {Blob} blob - The file content as a Blob
   * @param {string} fileName - The name of the file
   */
  static downloadFile(blob, fileName) {
    // Create a URL for the blob
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    
    // Append the link to the document body
    document.body.appendChild(link);
    
    // Click the link to trigger the download
    link.click();
    
    // Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);

    // Show a success message
    alert(`Download complete: ${fileName}`);
  }

  /**
   * Generate CSV content from data
   * @param {Object} options - Options for generating CSV
   * @param {Array} options.columns - Column definitions
   * @param {Array} options.data - Data to convert to CSV
   * @returns {string} - CSV content
   */
  static generateCSVContent({ columns, data }) {
    if (!data || data.length === 0) {
      return '';
    }

    // Create CSV header
    let csvContent = columns.map(col => `"${col.header}"`).join(',') + '\r\n';

    // Add data rows
    data.forEach((row, rowIndex) => {
      const csvRow = columns.map(column => {
        let value = column.value === 'index' ? (rowIndex + 1) : row[column.value];
        
        // Handle undefined values
        if (value === undefined || value === null) {
          value = '';
        }
        
        // Escape commas and quotes in text fields
        if (typeof value === 'string') {
          return `"${value.replace(/"/g, '""')}"`;
        }
        
        return value;
      });

      csvContent += csvRow.join(',') + '\r\n';
    });

    return csvContent;
  }

  /**
   * Generate XML content from data
   * @param {Object} options - Options for generating XML
   * @param {string} options.rootElement - Root XML element name
   * @param {string} options.childElement - Child XML element name
   * @param {Object} options.rootAttributes - Attributes for root element
   * @param {Array} options.data - Data to convert to XML
   * @param {Object} options.mapping - Mapping of data properties to XML elements
   * @returns {string} - XML content
   */
  static generateXMLContent({ rootElement, childElement, rootAttributes = {}, data, mapping }) {
    if (!data || data.length === 0) {
      return '';
    }

    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    
    // Start root element with attributes
    xml += `<${rootElement}`;
    Object.entries(rootAttributes).forEach(([key, value]) => {
      xml += ` ${key}="${this.escapeXmlAttribute(value)}"`;
    });
    xml += '>\n';

    // Add child elements
    data.forEach((item, index) => {
      xml += `  <${childElement}>\n`;
      
      // Add index if needed
      if (mapping.index) {
        xml += `    <${mapping.index}>${index + 1}</${mapping.index}>\n`;
      }
      
      // Add all mapped fields
      Object.entries(mapping).forEach(([dataField, xmlElement]) => {
        if (dataField !== 'index' && item[dataField] !== undefined) {
          xml += `    <${xmlElement}>${this.escapeXmlContent(item[dataField])}</${xmlElement}>\n`;
        }
      });
      
      xml += `  </${childElement}>\n`;
    });

    xml += `</${rootElement}>`;
    return xml;
  }

  /**
   * Escape special characters for XML content
   * @param {string|number} content - Content to escape
   * @returns {string} - Escaped content
   */
  static escapeXmlContent(content) {
    if (content === null || content === undefined) {
      return '';
    }
    
    const str = String(content);
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Escape special characters for XML attributes
   * @param {string|number} content - Content to escape
   * @returns {string} - Escaped content
   */
  static escapeXmlAttribute(content) {
    if (content === null || content === undefined) {
      return '';
    }
    
    const str = String(content);
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }
}

export default DownloadService;